import { useEffect } from "react";

import type { AppConfig } from "../../providers/ConfigProvider";

function underConstructionKey(
  environment: AppConfig["environment"] | undefined,
): string | undefined {
  if (environment === "production" || environment === "staging") {
    return `under_construction_${environment}`;
  }
  return undefined;
}

/**
 * Reloads when the under-construction flag changes, so the server decides
 * what shows next. The mount read only seeds the baseline, and only values
 * fetched from Remote Config count — a failed fetch never triggers a reload.
 * The SDK loads inside the effect, off every document's hydration path.
 */
export function useUnderConstructionReload(
  environment: AppConfig["environment"] | undefined,
): void {
  useEffect(() => {
    const key = underConstructionKey(environment);
    if (!key) return;

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void Promise.all([
      import("firebase/remote-config"),
      import("../../firebase-config"),
    ]).then(
      ([
        { fetchAndActivate, getValue, onConfigUpdate },
        { getRemoteConfigInstance },
      ]) => {
        if (cancelled) return;
        const remoteConfig = getRemoteConfigInstance();

        const remoteValue = (): boolean | undefined => {
          const value = getValue(remoteConfig, key);
          return value.getSource() === "remote" ? value.asBoolean() : undefined;
        };

        let seen: boolean | undefined;
        void fetchAndActivate(remoteConfig)
          .then(() => {
            seen = remoteValue();
          })
          .catch((error) =>
            console.error("Failed to fetch Remote Config", error),
          );

        unsubscribe = onConfigUpdate(remoteConfig, {
          next: async () => {
            try {
              await fetchAndActivate(remoteConfig);
              const value = remoteValue();
              if (value === undefined || value === seen) return;
              seen = value;
              window.location.reload();
            } catch (error) {
              console.error("Failed to apply Remote Config update", error);
            }
          },
          error: (error) => {
            console.error("Remote Config update listener error", error);
          },
          complete: () => {},
        });
      },
    );

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [environment]);
}
